import { useState, useEffect, useCallback } from 'react'
import posthog from 'posthog-js'
import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'

export type FeatureFlagValue = string | number | boolean | null

export interface UseFeatureFlagOptions {
  defaultValue?: FeatureFlagValue
  sendUsageEvent?: boolean
  eventProperties?: Record<string, unknown>
}

export interface UseFeatureFlagReturn {
  value: FeatureFlagValue
  isLoading: boolean
  isReady: boolean
  refresh: () => void
}

export { FeatureFlagKeys }

export const useFeatureFlag = <T extends keyof typeof FeatureFlagKeys>(
  flagKey: T,
  options: UseFeatureFlagOptions = {}
): UseFeatureFlagReturn => {
  const { defaultValue = null, sendUsageEvent = false, eventProperties = {} } = options

  const [value, setValue] = useState<FeatureFlagValue>(defaultValue)
  const [isLoading, setIsLoading] = useState(true)
  const [isReady, setIsReady] = useState(false)

  const checkFlag = useCallback(() => {
    if (!posthog || typeof posthog.getFeatureFlag !== 'function') {
      setValue(defaultValue)
      setIsLoading(false)
      setIsReady(false)
      return
    }

    try {
      const flagValue = posthog.getFeatureFlag(flagKey)
      setValue(flagValue !== undefined ? flagValue : defaultValue)
      setIsReady(true)

      if (sendUsageEvent && typeof posthog.capture === 'function') {
        posthog.capture('feature_flag_checked', {
          flag_key: flagKey,
          flag_value: flagValue,
          default_value: defaultValue,
          ...eventProperties,
        })
      }
    } catch {
      setValue(defaultValue)
      setIsReady(false)
    } finally {
      setIsLoading(false)
    }
  }, [flagKey, defaultValue, sendUsageEvent, eventProperties])

  const refresh = useCallback(() => {
    setIsLoading(true)
    checkFlag()
  }, [checkFlag])

  useEffect(() => {
    // Subscribe to flag changes — onFeatureFlags fires once flags are loaded
    // and on every subsequent flag update. Captures unsubscribe for cleanup.
    const unsub = posthog.onFeatureFlags(() => checkFlag())

    return () => {
      unsub?.()
    }
  }, [checkFlag])

  return { value, isLoading, isReady, refresh }
}
